import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  AlertColor,
  Tab,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import {
  useLazyAuthorizeSpotifyQuery,
  useExchangeSpotifyCodeMutation,
  useLazyGetSpotifyAdAccountsQuery,
} from "../../store/apis/spotifyApi";
import { useLazyGetAdvertisersQuery } from "../../store/apis/hivestackApi";
import {
  useLazyGetProfilesQuery as useLazyGetAmazonProfilesQuery,
  useLazyGetAdvertisersQuery as useLazyGetAmazonAdvertisersQuery,
} from "../../store/apis/amazonApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import type { components } from "../../types/api";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";

const DEFAULT_LIMIT = "20";
const DEFAULT_OFFSET = "0";
const AMAZON_REGIONS: AmazonRegion[] = ["EU", "NA"];

type SpotifyAdAccount = components["schemas"]["SpotifyAdAccount"];
type SpotifyPaging = components["schemas"]["SpotifyPaging"];
type SpotifyCodeExchangeResponse =
  components["schemas"]["SpotifyCodeExchangeResponse"];
type HivestackAdvertiser = components["schemas"]["HiveStackAdvertiser"];
type AmazonDspProfile = components["schemas"]["AmazonDspProfile"];
type AmazonDspAdvertiser = components["schemas"]["AmazonDspAdvertiser"];
type AmazonRegion = components["schemas"]["AmazonRegion"];

const Api: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>("spotify");
  const [authorizationCode, setAuthorizationCode] = useState<string>("");
  const [businessIdInput, setBusinessIdInput] = useState<string>("");
  const [limitInput, setLimitInput] = useState<string>(DEFAULT_LIMIT);
  const [offsetInput, setOffsetInput] = useState<string>(DEFAULT_OFFSET);
  const [amazonRegion, setAmazonRegion] = useState<AmazonRegion>(
    AMAZON_REGIONS[0]
  );
  const [amazonScope, setAmazonScope] = useState<string>("");

  const [lastExchangeResult, setLastExchangeResult] =
    useState<SpotifyCodeExchangeResponse | null>(null);
  const [oauthFeedback, setOauthFeedback] = useState<{
    severity: AlertColor;
    messageKey: string;
  } | null>(null);

  const [exchangeSpotifyCode, { isLoading: isExchanging }] =
    useExchangeSpotifyCodeMutation();
  const [triggerGetAdAccounts, { data: adAccountsResponse, isFetching }] =
    useLazyGetSpotifyAdAccountsQuery();
  const [authorizeSpotify, { isFetching: isAuthorizing }] =
    useLazyAuthorizeSpotifyQuery();
  const [
    triggerGetHivestackAdvertisers,
    {
      data: hivestackAdvertisers,
      isFetching: isFetchingHivestack,
      isUninitialized: isHivestackUninitialized,
      error: hivestackError,
    },
  ] = useLazyGetAdvertisersQuery();
  const [
    triggerGetAmazonProfiles,
    {
      data: amazonProfilesResponse,
      isFetching: isFetchingAmazonProfiles,
      isUninitialized: isAmazonProfilesUninitialized,
    },
  ] = useLazyGetAmazonProfilesQuery();
  const [
    triggerGetAmazonAdvertisers,
    {
      data: amazonAdvertisersResponse,
      isFetching: isFetchingAmazonAdvertisers,
      isUninitialized: isAmazonAdvertisersUninitialized,
    },
  ] = useLazyGetAmazonAdvertisersQuery();

  const adAccounts: SpotifyAdAccount[] = useMemo(
    () => adAccountsResponse?.data?.adAccounts ?? [],
    [adAccountsResponse]
  );

  const paging: SpotifyPaging | undefined = adAccountsResponse?.data?.paging;
  const amazonProfiles: AmazonDspProfile[] = useMemo(
    () => amazonProfilesResponse?.data ?? [],
    [amazonProfilesResponse]
  );
  const amazonAdvertisers: AmazonDspAdvertiser[] = useMemo(
    () => amazonAdvertisersResponse?.data?.response ?? [],
    [amazonAdvertisersResponse]
  );
  const amazonAdvertisersTotal = amazonAdvertisersResponse?.data?.totalResults;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const provider = searchParams.get("provider");
    const status = searchParams.get("status");
    const reason = searchParams.get("reason") ?? "generic";

    if (provider === "spotify" && status) {
      if (status === "success") {
        setOauthFeedback({
          severity: "success",
          messageKey: "api.spotify.oauth.success",
        });
      } else {
        setOauthFeedback({
          severity: "error",
          messageKey: `api.spotify.oauth.errors.${reason}`,
        });
      }

      searchParams.delete("provider");
      searchParams.delete("status");
      searchParams.delete("reason");
      const remaining = searchParams.toString();
      navigate(`${location.pathname}${remaining ? `?${remaining}` : ""}`, {
        replace: true,
      });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (hivestackError) {
      dispatch(
        setToastMessage({
          message: t("api.hivestack.fetchFailed", {
            defaultValue: "Failed to fetch Hivestack advertisers.",
          }),
          severity: "error",
        })
      );
      console.error("Failed to fetch Hivestack advertisers:", hivestackError);
    }
  }, [dispatch, hivestackError, t]);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    setActiveTab(value);
  };

  const handleAuthorizeSpotify = async () => {
    try {
      setOauthFeedback(null);
      const response = await authorizeSpotify().unwrap();
      const authorizeUrl = response.data?.authorizeUrl;

      if (authorizeUrl) {
        window.location.href = authorizeUrl;
        return;
      }

      throw new Error("Missing authorize URL");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("api.spotify.oauth.errors.generic", {
              defaultValue: "Failed to connect Spotify Ads.",
            });

      dispatch(
        setToastMessage({
          message: errorMessage,
          severity: "error",
        })
      );
    }
  };

  const handleFetchAmazonProfiles = async () => {
    try {
      await triggerGetAmazonProfiles(
        {
          region: amazonRegion,
        },
        true
      ).unwrap();
    } catch (error: unknown) {
      dispatch(
        setToastMessage({
          message: t("api.amazon.errors.profilesFetchFailed", {
            defaultValue: "Failed to fetch Amazon DSP profiles.",
          }),
          severity: "error",
        })
      );
      console.error("Failed to fetch Amazon DSP profiles", error);
    }
  };

  const handleFetchAmazonAdvertisers = async () => {
    const trimmedScope = amazonScope.trim();

    try {
      await triggerGetAmazonAdvertisers(
        {
          scope: trimmedScope || undefined,
        },
        true
      ).unwrap();
    } catch (error: unknown) {
      dispatch(
        setToastMessage({
          message: t("api.amazon.errors.advertisersFetchFailed", {
            defaultValue: "Failed to fetch Amazon DSP advertisers.",
          }),
          severity: "error",
        })
      );
      console.error("Failed to fetch Amazon DSP advertisers", error);
    }
  };

  const handleResetAmazonFilters = () => {
    setAmazonRegion(AMAZON_REGIONS[0]);
    setAmazonScope("");
  };

  const oauthAlertMessage = useMemo(() => {
    if (!oauthFeedback) {
      return null;
    }

    if (oauthFeedback.severity === "success") {
      return t("api.spotify.oauth.success", {
        defaultValue: "Spotify authorization completed successfully.",
      });
    }

    return t(oauthFeedback.messageKey, {
      defaultValue: t("api.spotify.oauth.errors.generic", {
        defaultValue: "Failed to connect Spotify Ads. Please try again.",
      }),
    });
  }, [oauthFeedback, t]);

  const handleExchangeCode = async () => {
    const trimmedCode = authorizationCode.trim();

    if (!trimmedCode) {
      dispatch(
        setToastMessage({
          message: t("api.spotify.errors.codeRequired", {
            defaultValue: "Authorization code is required.",
          }),
          severity: "error",
        })
      );
      return;
    }

    try {
      const response = await exchangeSpotifyCode({
        code: trimmedCode,
      }).unwrap();
      if (response.data) {
        setLastExchangeResult(response.data);
      }
      dispatch(
        setToastMessage({
          message: t("api.spotify.exchangeSuccess", {
            defaultValue: "Authorization code exchanged successfully.",
          }),
          severity: "success",
        })
      );
      setAuthorizationCode("");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("api.spotify.errors.exchangeFailed", {
              defaultValue: "Failed to exchange authorization code.",
            });

      dispatch(
        setToastMessage({
          message: errorMessage,
          severity: "error",
        })
      );
    }
  };

  const handleFetchAdAccounts = async () => {
    const trimmedBusinessId = businessIdInput.trim();
    const trimmedLimit = limitInput.trim();
    const trimmedOffset = offsetInput.trim();

    const parsedLimit = trimmedLimit ? Number(trimmedLimit) : undefined;
    const parsedOffset = trimmedOffset ? Number(trimmedOffset) : undefined;

    if (parsedLimit !== undefined && Number.isNaN(parsedLimit)) {
      dispatch(
        setToastMessage({
          message: t("api.spotify.errors.invalidLimit", {
            defaultValue: "Limit must be a valid number.",
          }),
          severity: "error",
        })
      );
      return;
    }

    if (parsedOffset !== undefined && Number.isNaN(parsedOffset)) {
      dispatch(
        setToastMessage({
          message: t("api.spotify.errors.invalidOffset", {
            defaultValue: "Offset must be a valid number.",
          }),
          severity: "error",
        })
      );
      return;
    }

    try {
      await triggerGetAdAccounts(
        {
          businessId: trimmedBusinessId || undefined,
          limit: parsedLimit,
          offset: parsedOffset,
        },
        true
      ).unwrap();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("api.spotify.errors.fetchFailed", {
              defaultValue: "Failed to fetch Spotify ad accounts.",
            });

      dispatch(
        setToastMessage({
          message: errorMessage,
          severity: "error",
        })
      );
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ display: "flex" }}>
        <Sidebar />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            transition: "margin-left 0.3s ease",
            marginLeft: collapsed ? "80px" : "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t("common.unauthorized", {
              defaultValue: "You are not authorized to view this page.",
            })}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h3" gutterBottom>
                {t("api.title", { defaultValue: "API Integrations" })}
              </Typography>
              <Typography
                variant="pSmallest"
                gutterBottom
                color={theme.palette.neutral[600]}
              >
                {t("api.subtitle", {
                  defaultValue:
                    "Manage external platform integrations and authentication flows.",
                })}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 3 }}>
            <Tab
              label={t("api.spotify.tabLabel", { defaultValue: "Spotify" })}
              value="spotify"
            />
            <Tab
              label={t("api.hivestack.tabLabel", { defaultValue: "Hivestack" })}
              value="hivestack"
            />
            <Tab
              label={t("api.amazon.tabLabel", { defaultValue: "Amazon DSP" })}
              value="amazon"
            />
          </Tabs>

          {activeTab === "spotify" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, px: 3 }}
            >
              <Alert severity="info">
                {t("api.spotify.instructions", {
                  defaultValue:
                    "Follow the Spotify Ads OAuth flow to obtain an authorization code, then exchange it for long-lived credentials and fetch ad accounts.",
                })}
              </Alert>

              {oauthFeedback && oauthAlertMessage && (
                <Alert severity={oauthFeedback.severity}>
                  {oauthAlertMessage}
                </Alert>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6">
                  {t("api.spotify.exchangeTitle", {
                    defaultValue: "Exchange Authorization Code",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("api.spotify.exchangeDescription", {
                    defaultValue:
                      "Paste the authorization code obtained from the Spotify Ads authorization URL. The backend will store the refresh token and latest access token.",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("api.spotify.connectDescription", {
                    defaultValue:
                      "Use the button below to be redirected to Spotify and grant BeApolo access to your ad accounts.",
                  })}
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleAuthorizeSpotify}
                    disabled={isAuthorizing}
                    startIcon={
                      isAuthorizing ? <CircularProgress size={16} /> : undefined
                    }
                  >
                    {t("api.spotify.connectButton", {
                      defaultValue: "Connect Spotify Ads",
                    })}
                  </Button>
                </Box>

                <TextField
                  label={t("api.spotify.authorizationCodeLabel", {
                    defaultValue: "Authorization Code",
                  })}
                  value={authorizationCode}
                  onChange={(event) => setAuthorizationCode(event.target.value)}
                  placeholder={t("api.spotify.authorizationCodePlaceholder", {
                    defaultValue: "Enter the code provided by Spotify",
                  })}
                  fullWidth
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleExchangeCode}
                    disabled={isExchanging}
                    startIcon={
                      isExchanging ? <CircularProgress size={16} /> : undefined
                    }
                  >
                    {t("api.spotify.exchangeButton", {
                      defaultValue: "Exchange Code",
                    })}
                  </Button>
                </Box>

                {lastExchangeResult && (
                  <Alert severity="success">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {t("api.spotify.lastExchangeTitle", {
                        defaultValue: "Last exchange result",
                      })}
                    </Typography>
                    <Typography variant="body2">
                      {t("api.spotify.accessTokenExpires", {
                        defaultValue: "Access token expires at {{value}}",
                        value: lastExchangeResult.expiresAt,
                      })}
                    </Typography>
                    <Typography variant="body2">
                      {t("api.spotify.refreshTokenAvailable", {
                        defaultValue: "Refresh token stored and ready for use.",
                      })}
                    </Typography>
                  </Alert>
                )}
              </Box>

              <Divider />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6">
                  {t("api.spotify.adAccountsTitle", {
                    defaultValue: "Fetch Spotify Ad Accounts",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("api.spotify.adAccountsDescription", {
                    defaultValue:
                      "Provide an optional business ID to override the default configuration. Limit and offset help paginate through results.",
                  })}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    label={t("api.spotify.businessIdLabel", {
                      defaultValue: "Business ID (optional)",
                    })}
                    value={businessIdInput}
                    onChange={(event) => setBusinessIdInput(event.target.value)}
                    placeholder={t("api.spotify.businessIdPlaceholder", {
                      defaultValue: "74f94499-db6e-4abb-add5-a8056c68e324",
                    })}
                    fullWidth
                  />
                  <TextField
                    label={t("api.spotify.limitLabel", {
                      defaultValue: "Limit",
                    })}
                    value={limitInput}
                    onChange={(event) => setLimitInput(event.target.value)}
                    placeholder={DEFAULT_LIMIT}
                    fullWidth
                  />
                  <TextField
                    label={t("api.spotify.offsetLabel", {
                      defaultValue: "Offset",
                    })}
                    value={offsetInput}
                    onChange={(event) => setOffsetInput(event.target.value)}
                    placeholder={DEFAULT_OFFSET}
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleFetchAdAccounts}
                    disabled={isFetching}
                    startIcon={
                      isFetching ? <CircularProgress size={16} /> : undefined
                    }
                  >
                    {t("api.spotify.getAccountsButton", {
                      defaultValue: "Fetch ad accounts",
                    })}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setBusinessIdInput("");
                      setLimitInput(DEFAULT_LIMIT);
                      setOffsetInput(DEFAULT_OFFSET);
                    }}
                  >
                    {t("common.reset", { defaultValue: "Reset" })}
                  </Button>
                </Box>

                {isFetching && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}

                {!isFetching && adAccounts.length > 0 && (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("api.spotify.table.name", {
                              defaultValue: "Name",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.spotify.table.id", { defaultValue: "ID" })}
                          </TableCell>
                          <TableCell>
                            {t("api.spotify.table.status", {
                              defaultValue: "Status",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.spotify.table.currency", {
                              defaultValue: "Currency",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.spotify.table.timezone", {
                              defaultValue: "Timezone",
                            })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adAccounts.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell>{account.name}</TableCell>
                            <TableCell>{account.id}</TableCell>
                            <TableCell>{account.status ?? "-"}</TableCell>
                            <TableCell>{account.currency ?? "-"}</TableCell>
                            <TableCell>{account.timezone ?? "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {paging && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 1 }}
                      >
                        {t("api.spotify.paginationSummary", {
                          defaultValue:
                            "Page size {{pageSize}}, {{total}} total results, offset {{offset}}.",
                          pageSize: paging.page_size,
                          total: paging.total_results,
                          offset: paging.offset,
                        })}
                      </Typography>
                    )}
                  </Box>
                )}

                {!isFetching &&
                  adAccounts.length === 0 &&
                  adAccountsResponse && (
                    <Alert severity="info">
                      {t("api.spotify.noAdAccounts", {
                        defaultValue:
                          "No ad accounts found for the provided criteria.",
                      })}
                    </Alert>
                  )}
              </Box>
            </Box>
          )}

          {activeTab === "hivestack" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, px: 3 }}
            >
              <Alert severity="info">
                {t("api.hivestack.instructions", {
                  defaultValue:
                    "Use the button below to call /api/hivestack/advertisers and retrieve your latest Hivestack advertisers.",
                })}
              </Alert>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!isAdmin) {
                      return;
                    }
                    triggerGetHivestackAdvertisers();
                  }}
                  disabled={isFetchingHivestack}
                  startIcon={
                    isFetchingHivestack ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {t("api.hivestack.fetchButton", {
                    defaultValue: "Fetch advertisers",
                  })}
                </Button>
              </Box>

              {isFetchingHivestack ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (hivestackAdvertisers?.length ?? 0) > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t("api.hivestack.table.name", {
                            defaultValue: "Name",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("api.hivestack.table.organizationId", {
                            defaultValue: "Organization ID",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("api.hivestack.table.advertiserId", {
                            defaultValue: "Advertiser ID",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("api.hivestack.table.domains", {
                            defaultValue: "Domains",
                          })}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(hivestackAdvertisers ?? []).map(
                        (advertiser: HivestackAdvertiser) => (
                          <TableRow key={advertiser.advertiser_id}>
                            <TableCell>
                              {advertiser.unique_advertiser?.name ?? "-"}
                            </TableCell>
                            <TableCell>{advertiser.organization_id}</TableCell>
                            <TableCell>{advertiser.advertiser_id}</TableCell>
                            <TableCell>
                              {advertiser.unique_advertiser?.advertiser_domains
                                ?.length
                                ? advertiser.unique_advertiser.advertiser_domains
                                    .map((domain) => domain.domain)
                                    .join(", ")
                                : "-"}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Alert severity="info">
                  {isHivestackUninitialized
                    ? t("api.hivestack.initialPrompt", {
                        defaultValue:
                          "Click the button above to load advertisers.",
                      })
                    : t("api.hivestack.noAdvertisers", {
                        defaultValue: "No Hivestack advertisers found.",
                      })}
                </Alert>
              )}
            </Box>
          )}

          {activeTab === "amazon" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, px: 3 }}
            >
              <Alert severity="info">
                {t("api.amazon.instructions", {
                  defaultValue:
                    "Select a region and optionally a scope to fetch Amazon DSP profiles and advertisers.",
                })}
              </Alert>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <TextField
                  select
                  label={t("api.amazon.regionLabel", {
                    defaultValue: "Region",
                  })}
                  value={amazonRegion}
                  onChange={(event) =>
                    setAmazonRegion(event.target.value as AmazonRegion)
                  }
                >
                  {AMAZON_REGIONS.map((region) => (
                    <MenuItem key={region} value={region}>
                      {t(`api.amazon.regions.${region.toLowerCase()}`, {
                        defaultValue: region,
                      })}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("api.amazon.scopeLabel", {
                    defaultValue: "Scope (optional)",
                  })}
                  value={amazonScope}
                  onChange={(event) => setAmazonScope(event.target.value)}
                  placeholder="advertiser"
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={handleFetchAmazonProfiles}
                  disabled={isFetchingAmazonProfiles}
                  startIcon={
                    isFetchingAmazonProfiles ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {t("api.amazon.fetchProfiles", {
                    defaultValue: "Fetch profiles",
                  })}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleFetchAmazonAdvertisers}
                  disabled={isFetchingAmazonAdvertisers}
                  startIcon={
                    isFetchingAmazonAdvertisers ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {t("api.amazon.fetchAdvertisers", {
                    defaultValue: "Fetch advertisers",
                  })}
                </Button>
                <Button variant="text" onClick={handleResetAmazonFilters}>
                  {t("common.reset", { defaultValue: "Reset" })}
                </Button>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6">
                  {t("api.amazon.profiles.title", { defaultValue: "Profiles" })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("api.amazon.profiles.description", {
                    defaultValue:
                      "View DSP profiles returned for the selected region.",
                  })}
                </Typography>

                {isFetchingAmazonProfiles ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                ) : amazonProfiles.length > 0 ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("api.amazon.profiles.table.accountName", {
                              defaultValue: "Account name",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.accountId", {
                              defaultValue: "Account ID",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.accountType", {
                              defaultValue: "Account type",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.marketplaceId", {
                              defaultValue: "Marketplace ID",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.profileId", {
                              defaultValue: "Profile ID",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.country", {
                              defaultValue: "Country",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.currency", {
                              defaultValue: "Currency",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.profiles.table.timezone", {
                              defaultValue: "Timezone",
                            })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {amazonProfiles.map((profile) => (
                          <TableRow key={profile.profileId}>
                            <TableCell>{profile.accountInfo.name}</TableCell>
                            <TableCell>{profile.accountInfo.id}</TableCell>
                            <TableCell>{profile.accountInfo.type}</TableCell>
                            <TableCell>
                              {profile.accountInfo.marketplaceStringId}
                            </TableCell>
                            <TableCell>{profile.profileId}</TableCell>
                            <TableCell>{profile.countryCode}</TableCell>
                            <TableCell>{profile.currencyCode}</TableCell>
                            <TableCell>{profile.timezone}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Alert severity="info">
                    {isAmazonProfilesUninitialized
                      ? t("api.amazon.profiles.initialPrompt", {
                          defaultValue: "Fetch profiles to view results.",
                        })
                      : t("api.amazon.profiles.empty", {
                          defaultValue: "No profiles returned for this region.",
                        })}
                  </Alert>
                )}
              </Box>

              <Divider />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6">
                  {t("api.amazon.advertisers.title", {
                    defaultValue: "Advertisers",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("api.amazon.advertisers.description", {
                    defaultValue:
                      "Advertisers associated with the selected region and optional scope.",
                  })}
                </Typography>

                {isFetchingAmazonAdvertisers ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                ) : amazonAdvertisers.length > 0 ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("api.amazon.advertisers.table.name", {
                              defaultValue: "Advertiser name",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.advertisers.table.advertiserId", {
                              defaultValue: "Advertiser ID",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.advertisers.table.country", {
                              defaultValue: "Country",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.advertisers.table.timezone", {
                              defaultValue: "Timezone",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.advertisers.table.currency", {
                              defaultValue: "Currency",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.advertisers.table.url", {
                              defaultValue: "URL",
                            })}
                          </TableCell>
                          <TableCell>
                            {t("api.amazon.advertisers.table.isRegional", {
                              defaultValue: "Is regional",
                            })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {amazonAdvertisers.map((advertiser) => (
                          <TableRow key={advertiser.advertiserId}>
                            <TableCell>{advertiser.name}</TableCell>
                            <TableCell>{advertiser.advertiserId}</TableCell>
                            <TableCell>{advertiser.country}</TableCell>
                            <TableCell>{advertiser.timezone}</TableCell>
                            <TableCell>{advertiser.currency}</TableCell>
                            <TableCell>
                              {advertiser.url ? (
                                <a
                                  href={advertiser.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {advertiser.url}
                                </a>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>
                              {advertiser.isRegional
                                ? t("common.yes", { defaultValue: "Yes" })
                                : t("common.no", { defaultValue: "No" })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {typeof amazonAdvertisersTotal === "number" && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 1 }}
                      >
                        {t("api.amazon.advertisers.totalResults", {
                          defaultValue: "Total results: {{total}}",
                          total: amazonAdvertisersTotal,
                        })}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Alert severity="info">
                    {isAmazonAdvertisersUninitialized
                      ? t("api.amazon.advertisers.initialPrompt", {
                          defaultValue: "Fetch advertisers to view results.",
                        })
                      : t("api.amazon.advertisers.empty", {
                          defaultValue:
                            "No advertisers returned for this filter.",
                        })}
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default Api;
